// dependencies
import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";

// hooks
import { useCustomFactorsIncludedInSatisfaction, useFactorsSummary } from "../../../../api/hooks";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";

// api
import { ICohortModel, getFactorsSummary } from "../../../../api/services";

import { getColor } from "../../../../utils/getColor";
// utils
import { Hex } from "../../../../utils/getContrast";

// config
import Config from "../../Config";
import { IEngagementFilter, ISummaryRequestParams } from "../../schema";

// constants, opacity in hex
const SELECTED_BACKGROUND_OPACITY = "26"; // 15%
const UNSELECTED_BACKGROUND_OPACITY = "1a"; // 10%
const UNSELECTED_POINT_AND_BORDER_OPACITY = "33"; // 20%

interface ChartProps {
	filter?: IEngagementFilter;
	month?: Date;
	selectedCohorts?: ICohortModel[];
	cohort?: string;
	showAllEmployees?: boolean;
}

const useFactorsPolygonChart = (props: ChartProps) => {
	const { filter, month, selectedCohorts = [], cohort, showAllEmployees = true } = props;
	const { value: customFactorsEnabled } = useFeatureFlagQuery("ff-engagement-custom-factors", false);
	const { data: customFactors } = useCustomFactorsIncludedInSatisfaction({ enabled: customFactorsEnabled });

	let customFactorsIncludedInOverallSatisfaction = [];
	if (customFactorsEnabled) {
		customFactorsIncludedInOverallSatisfaction = customFactors?.customFactors?.map((factor) => factor.name) || [];
	}

	const allFactors = [...Config.FACTORS.FACTORS, ...customFactorsIncludedInOverallSatisfaction].sort();

	// chart data
	const {
		data: allEmployeesFactors,
		isLoading: isAllEmployeesFactorsLoading,
		isError: allEmployeesFactorsError,
	} = useFactorsSummary({
		month: month,
		startDate: filter?.startDate,
		endDate: filter?.endDate,
		binSize: Config.BIN_SIZE,
		overall: "true",
	});
	/**
	 * Calculate the average of each factor for the given period for all employees
	 */
	const allEmployeesFactorsData = useMemo(() => {
		// create a map of all factors with empty arrays
		const factorsMap = allFactors.reduce(
			(acc, factor) => {
				acc[factor] = [];
				return acc;
			},
			{} as { [key: string]: number[] }
		);

		// populate the map with the average of each factor from API response
		allEmployeesFactors?.data.forEach((factorData) => {
			if (allFactors.includes(factorData.factor) && factorData.average) {
				factorsMap[factorData.factor].push(factorData.average);
			}
		});
		// return a list of averages for each factor in the same order as allFactors
		return allFactors.map((factor) => {
			return factorsMap[factor].length > 0
				? factorsMap[factor].reduce((acc, value) => acc + value, 0) / factorsMap[factor].length
				: null;
		});
	}, [allEmployeesFactors?.data]);

	// fetch data for selected cohorts
	const selectedCohortsFactorsResponses = useQueries({
		queries: [
			...selectedCohorts.map((cohort) => {
				const params = {
					month: month,
					startDate: filter?.startDate,
					endDate: filter?.endDate,
					binSize: Config.BIN_SIZE,
					cohortIds: cohort._id,
					overall: "true",
				} as ISummaryRequestParams;
				return {
					queryKey: ["factor_summary", params, cohort._id],
					queryFn: async () => {
						const result = await getFactorsSummary(params);
						return {
							...result,
							cohortID: cohort._id,
						};
					},
				};
			}),
		],
	});

	const isSelectedCohortsLoading = selectedCohortsFactorsResponses.some((result) => result.isLoading);
	const selectedCohortsError = selectedCohortsFactorsResponses.some((result) => result.isError);

	// calculate factor averages for selected cohorts
	const selectedCohortsFactorResponseAverages = useMemo(() => {
		if (selectedCohortsFactorsResponses && selectedCohortsFactorsResponses.length && !isSelectedCohortsLoading) {
			// for each cohort selected, calcuate the factor averages
			const factorsAverages = selectedCohortsFactorsResponses.reduce((responseAverages, factor) => {
				// create lookup for all factor averages
				// {
				// 	factor1: [1, 2, 3],
				// 	factor2: [3, 3, 3],
				// 	...
				// }
				const factorAveragesMap = factor?.data?.data.reduce((acc, val) => {
					if (!acc[val.factor]) {
						acc[val.factor] = val.average ? [val.average] : null;
					} else {
						acc[val.factor].push(val.average);
					}
					return acc;
				}, {});

				// format the averages to match the order of allFactors (order of radar chart labels)
				const averages = allFactors.map((factor) => {
					if (factorAveragesMap && factorAveragesMap[factor]) {
						const average =
							factorAveragesMap[factor].reduce((a, b) => a + b) / factorAveragesMap[factor].length;

						const formattedAverage = average.toFixed(2);
						return formattedAverage;
					}
					return null;
				});

				if (factor.data?.cohortID) {
					responseAverages[factor.data.cohortID] = averages;
				}

				return responseAverages;
			}, {});

			return factorsAverages;
		}
	}, [JSON.stringify(selectedCohortsFactorsResponses)]);

	// format all selected cohorts datasets to be used in the chart
	const selectedCohortsDatasets = useMemo(
		() => [
			...selectedCohorts.map((cohortModel) => {
				const cohortColor = cohortModel.color as Hex;

				// we want to highlight the dataset (put it into focus) if:
				// 1. there is no cohort selected
				// 2. the cohort selected is the same as the current cohort
				const highlightDataset = !cohort || cohort === cohortModel._id;
				return {
					id: cohortModel._id,
					label: cohortModel.name,
					data: selectedCohortsFactorResponseAverages?.[cohortModel._id] || [],
					backgroundColor: highlightDataset
						? cohortColor + SELECTED_BACKGROUND_OPACITY
						: cohortColor + UNSELECTED_BACKGROUND_OPACITY,
					borderColor: highlightDataset ? cohortColor : cohortColor + UNSELECTED_POINT_AND_BORDER_OPACITY,
					pointBackgroundColor: highlightDataset
						? cohortColor
						: cohortColor + UNSELECTED_POINT_AND_BORDER_OPACITY,
					borderWidth: 2,
					pointRadius: highlightDataset ? 2 : 0,
					pointHoverRadius: highlightDataset ? 4 : 2,
					order: cohort === cohortModel._id ? 1 : 0,
				};
			}),
		],
		[JSON.stringify(selectedCohortsFactorResponseAverages), JSON.stringify(filter?.startDate), cohort]
	);

	const highlightAllEmployeesDataset = !cohort || cohort === "all";
	const allEmployeesColor = getColor("primary", 5);

	const allEmployeesDataset = {
		id: "all",
		label: "All Employees",
		data: allEmployeesFactorsData,
		backgroundColor: highlightAllEmployeesDataset
			? allEmployeesColor + SELECTED_BACKGROUND_OPACITY
			: allEmployeesColor + UNSELECTED_BACKGROUND_OPACITY,
		borderColor: highlightAllEmployeesDataset ? allEmployeesColor : allEmployeesColor + "33",
		pointBackgroundColor: highlightAllEmployeesDataset ? allEmployeesColor : allEmployeesColor + "33",
		borderWidth: 2,
		pointRadius: highlightAllEmployeesDataset ? 2 : 0,
		pointHoverRadius: highlightAllEmployeesDataset ? 4 : 2,
		order: cohort === "all" ? 1 : 0,
	};

	return {
		orderedDataSets: showAllEmployees
			? [...selectedCohortsDatasets, allEmployeesDataset]
			: [...selectedCohortsDatasets],
		isLoadingData: showAllEmployees ? isAllEmployeesFactorsLoading : isSelectedCohortsLoading,
		hasError: allEmployeesFactorsError || selectedCohortsError,
	};
};

export default useFactorsPolygonChart;
